import React from 'react';
import { Box, Button, Text, Icon, Flex, IconName, ReadMore } from '@fullstory/components';
import { FormattedMessage } from 'react-intl';
import { getFormattedRelativeTime } from '@fullstory/util/date';
import css from './index.module.scss';
import { Notification, NotificationContent, Facility } from '@fullstory/proto/fs/services/pinguin';
import { UnreadNotificationDot } from '../../UnreadNotificationDot';
import { Link } from 'react-router-dom';

export interface NotificationItemProps {
  notification: Notification;
  showTimestamp?: boolean;
  closeProps?: {
    onClose: () => void;
    cta: string;
  };
  onClickCta?: () => void;
}
export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  showTimestamp,
  closeProps,
  onClickCta,
}) => {
  const { content, createdAt, count } = notification;
  const fullContent = `${content?.preheader ? `${content?.preheader} ` : ''}${content?.content}`;

  const getName = (notification: Notification): IconName => {
    switch (notification.configId?.facility) {
      case Facility.METRIC_ALERTS:
        return 'metrics';
      case Facility.SEGMENT_ALERTS:
        return 'segments';
      case Facility.ACCOUNT_MANAGEMENT:
        return 'account';
      case Facility.NOTES:
        return 'comment-note';
      case Facility.OPPORTUNITIES:
        return 'metric-insight';
      default:
        return 'bell';
    }
  };

  return (
    <Box
      data-component="NotificationItem"
      padding={12}
      className={`${css.notificationItem}`}
      testId="notification-item"
    >
      <Flex alignItems="flex-start" justifyContent="flex-start">
        <Box marginLeft={4} marginRight={16}>
          <Flex alignItems="center" className={css.iconContainer}>
            <UnreadNotificationDot unread={!notification.read}>
              <Icon size={20} name={getName(notification)} />
            </UnreadNotificationDot>
          </Flex>
        </Box>
        <Box flexGrow={1}>
          <Box marginBottom={4}>
            <Text appearance="bodyMediumBold">{content?.title}</Text>
          </Box>
          <Box marginBottom={16} paddingRight={16}>
            <Text appearance="bodySmall" color="secondary" tag="div">
              <ReadMore content={fullContent} maxLines={2} />
            </Text>
          </Box>

          <Box>
            <Flex gap={8} justifyContent="flex-start">
              <LinkButton content={content} onClickCta={onClickCta} />
              {closeProps && (
                <Button
                  onClick={closeProps.onClose}
                  size="x-small"
                  justifyContent="center"
                  variant="minimal"
                  testId="dismiss-button"
                >
                  {closeProps.cta}
                </Button>
              )}
            </Flex>
          </Box>

          <Box marginTop={8}>
            {showTimestamp && createdAt && (
              <Text appearance="bodyXSmall" tag="span">
                {getFormattedRelativeTime(new Date(createdAt.toString()))}
              </Text>
            )}

            {count > 1 && (
              <Box marginLeft={showTimestamp ? 8 : 0} inline>
                <Text appearance="bodyXSmallBold">
                  <FormattedMessage
                    defaultMessage="happened {count} previous {count, plural, =1 {time} other {times}}"
                    id="p4IWXK"
                    values={{ count: count - 1 }}
                  />
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

interface LinkButtonProps
  extends Pick<React.ComponentProps<typeof NotificationItem>, 'onClickCta'> {
  content: NotificationContent | null;
}

const LinkButton: React.FC<LinkButtonProps> = ({ content, onClickCta }) => {
  if (!content || !content.ctaUrl || !content.ctaLabel) {
    return <></>;
  }

  let linkPath = '';
  try {
    const parsed = new URL(content.ctaUrl);
    if (parsed.origin == window.location.origin) {
      // URL base is the same, so strip off the base portion and use a Link with a path
      linkPath = parsed.pathname + parsed.search + parsed.hash;
    }
    // Otherwise, it's likely either an external link or a mailto link,
    // so just use a regular href.
  } catch {
    // It isn't parsable, so we assume it is a path with the same base and will use Link.
    // TODO(sarahgrace): Add /ui/<orgId> if missing.
    linkPath = content.ctaUrl;
  }

  if (linkPath) {
    return (
      <Button
        tag={Link}
        to={linkPath}
        onClick={onClickCta}
        size="x-small"
        justifyContent="center"
        variant="fill-secondary"
      >
        {content.ctaLabel}
      </Button>
    );
  }

  return (
    <Button
      data-component="LinkButton"
      tag="a"
      href={content.ctaUrl}
      target="_blank"
      onClick={onClickCta}
      size="x-small"
      justifyContent="center"
      variant="fill-secondary"
    >
      {content.ctaLabel}
    </Button>
  );
};
